import { Component } from '@theme/component';

/**
 * `<care-guide-video>` — click-to-play for the guide video in the shoe care
 * drawer (blocks/product-care-guide.liquid).
 *
 * Playback is the shopper's call: the markup carries no `autoplay` and
 * `preload: 'none'`, and the clip plays with sound. A hidden media element is not
 * eligible for autoplay in any useful way either (Chrome would start it
 * invisibly, buffering 12 MB of a closed drawer's clip on every PDP, while Safari
 * refused outright), so an unopened drawer costs nothing.
 *
 * What the shopper presses is the poster button over the video, not the video's
 * own control art — the drawer wants one centred play affordance, and a paused
 * `<video controls>` gets whatever each browser decides to draw. The button is
 * `display: none` until this element upgrades, so a page where this module never
 * runs still shows the video and its native controls rather than a dead button.
 * Once the video reports `playing` the button is removed for good and the native
 * controls take over; a `play()` that never gets there (a power-saving or
 * autoplay refusal) leaves the button in place to be pressed again.
 *
 * `theme-drawer.js` dispatches `theme-drawer:close` on the `<theme-drawer>`
 * element, so the pause listens there rather than tracking the dialog's `open`
 * attribute itself. It matters more now than it did muted: a video left running
 * behind a closed drawer keeps talking with no visible control to stop it.
 */
class CareGuideVideo extends Component {
  /**
   * Built per connection, not once per instance: `disconnectedCallback` aborts it,
   * and an element that is re-inserted (a section morph, a drawer moved in the DOM)
   * runs `connectedCallback` again. A single controller created at construction
   * would already be aborted by then, so the listeners below would register against
   * a dead signal and the video would never play again.
   * @type {AbortController | undefined}
   */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#poster?.addEventListener('click', this.#play, { signal });

    // `playing` rather than the click, so the poster only lifts once there is a
    // frame behind it to lift onto — with `preload: 'none'` the first press is
    // also the first byte, and hiding it on click would flash the empty element
    // for as long as the network takes.
    this.#video?.addEventListener('playing', this.#hidePoster, { signal });

    this.closest('theme-drawer')?.addEventListener('theme-drawer:close', this.#pause, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {HTMLVideoElement | null} */
  get #video() {
    return this.querySelector('video');
  }

  /** @returns {HTMLButtonElement | null} */
  get #poster() {
    return this.querySelector('.product-care-guide__video-poster');
  }

  #play = () => {
    // A rejected play() is a normal outcome — a power-saving or autoplay refusal,
    // not an error. The poster stays up in that case, so there is nothing to
    // recover from and nothing worth logging.
    this.#video?.play().catch(() => {});
  };

  #hidePoster = () => {
    const poster = this.#poster;
    if (!poster) return;

    // Moving focus first: the press that started playback left focus on a button
    // about to become `display: none`, which would drop the focus ring to the
    // top of the drawer. The video takes it instead, so the next Tab continues
    // through its controls and Space plays/pauses where the shopper is looking.
    if (poster.contains(document.activeElement)) this.#video?.focus();

    poster.hidden = true;
  };

  #pause = () => {
    this.#video?.pause();
  };
}

if (!customElements.get('care-guide-video')) {
  customElements.define('care-guide-video', CareGuideVideo);
}
